import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Wallet, Calendar, TrendingUp, Clock, CheckCircle2, FileText, Euro, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const fmtEUR = (v) => new Intl.NumberFormat('it-IT', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(Number(v || 0));

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

// Preset periodo → { from, to, label }
const buildPreset = (key) => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const iso = (d) => d.toISOString().slice(0, 10);
  switch (key) {
    case 'this_month':
      return { from: iso(new Date(Date.UTC(y, m, 1))), to: iso(new Date(Date.UTC(y, m + 1, 0))) };
    case 'last_month':
      return { from: iso(new Date(Date.UTC(y, m - 1, 1))), to: iso(new Date(Date.UTC(y, m, 0))) };
    case 'this_year':
      return { from: iso(new Date(Date.UTC(y, 0, 1))), to: iso(new Date(Date.UTC(y, 11, 31))) };
    case 'last_90':
    default: {
      const to = new Date(now);
      const from = new Date(now); from.setUTCDate(from.getUTCDate() - 90);
      return { from: iso(from), to: iso(to) };
    }
  }
};

const PERIOD_LABELS = {
  get this_month() { return i18n.t('commissions.period.thisMonth', 'Questo mese'); },
  get last_month() { return i18n.t('commissions.period.lastMonth', 'Mese scorso'); },
  get this_year() { return i18n.t('commissions.period.thisYear', 'Anno corrente'); },
  get last_90() { return i18n.t('commissions.period.last90', 'Ultimi 90 gg'); },
};

/**
 * ★ 2026-08-13 (Fase 46) — MyCommissionsCard
 *
 * Widget dashboard agente. Mostra:
 * - Totale guadagno nel periodo filtrato
 * - Prossimo incasso (data + importo + giorni residui)
 * - Breakdown per stato (pending/unlocked/invoiced/paid)
 *
 * Polling 60s + on visibility change → dati sempre aggiornati.
 */
export default function MyCommissionsCard() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('this_month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const { from, to } = useMemo(() => buildPreset(period), [period]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/agent-commissions/summary', {
        params: { from_date: from, to_date: to },
      });
      setData(d);
    } catch (e) {
      /* silent — potrebbe non essere agente */
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);

  if (loading) {
    return (
      <Card className="p-5 md:p-6 animate-pulse">
        <div className="h-4 w-40 bg-[var(--surface-2)] rounded mb-3" />
        <div className="h-10 w-32 bg-[var(--surface-2)] rounded" />
      </Card>
    );
  }

  if (!data) return null;

  const bd = data.breakdown || {};

  return (
    <Card className="p-4 md:p-6" data-testid="my-commissions-card">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground inline-flex items-center gap-1.5">
            <Wallet className="w-3 h-3 text-[var(--gp-gold)]" />
            {t('commissions.myTitle', 'Le mie commissioni')}
          </div>
          <h3 className="font-serif text-xl md:text-2xl mt-1">{t('commissions.earnings', 'Guadagno')} {PERIOD_LABELS[period].toLowerCase()}</h3>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[170px] h-9" data-testid="mycomm-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PERIOD_LABELS).map(([k, l]) => (
              <SelectItem key={k} value={k}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Totale periodo */}
        <div className="rounded-xl bg-gradient-to-br from-[var(--gp-gold)]/12 via-[var(--gp-gold)]/6 to-transparent border border-[var(--gp-gold)]/25 p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            {t('commissions.earnings', 'Guadagno')} {PERIOD_LABELS[period].toLowerCase()}
          </div>
          <div className="font-serif text-3xl md:text-4xl text-[var(--gp-gold)] tabular-nums mt-1" data-testid="mycomm-period-total">
            {fmtEUR(data.period_total)}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{data.period_count} {t('commissions.unitLabel', 'commissioni')}</div>
        </div>

        {/* Prossimo incasso */}
        <div className="rounded-xl border border-border p-4 bg-[var(--surface-1)]">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {t('commissions.nextIncome', 'Prossimo incasso')}
          </div>
          {data.next_commission ? (
            <>
              <div className="font-serif text-2xl md:text-3xl text-[hsl(var(--primary))] tabular-nums mt-1" data-testid="mycomm-next-amount">
                {fmtEUR(data.next_commission.amount)}
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                <span>{fmtDate(data.next_commission.unlock_date)}</span>
                {typeof data.next_commission.days_to_unlock === 'number' && (
                  <Badge variant="outline" className="text-[10px] py-0 h-5">
                    {t('commissions.inDays', 'tra {{n}} giorni', { n: data.next_commission.days_to_unlock })}
                  </Badge>
                )}
              </div>
              {data.next_commission.lead_name && (
                <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                  {t('commissions.closing', 'Chiusura:')} {data.next_commission.lead_name}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="font-serif text-lg text-muted-foreground mt-1">{t('commissions.noneScheduled', 'Nessun incasso in programma')}</div>
              <div className="text-xs text-muted-foreground">{t('commissions.noneScheduledHint', 'Chiudi una nuova pratica per generare la prossima commissione.')}</div>
            </>
          )}
        </div>
      </div>

      {/* Breakdown per stato */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
        <BreakdownItem icon={Clock}       label={t('commissions.status.pending', 'In attesa')}  value={bd.pending}   color="text-amber-700"   />
        <BreakdownItem icon={CheckCircle2} label={t('commissions.status.unlocked', 'Sbloccate')} value={bd.unlocked}  color="text-emerald-700" />
        <BreakdownItem icon={FileText}    label={t('commissions.status.invoiced', 'Fatturate')}  value={bd.invoiced}  color="text-blue-700"    />
        <BreakdownItem icon={Euro}        label={t('commissions.status.paid', 'Incassate')}      value={bd.paid}      color="text-violet-700"  />
      </div>

      {/* Upcoming list preview */}
      {data.upcoming && data.upcoming.length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{t('commissions.upcoming', 'Prossimi incassi')}</div>
          <div className="space-y-1.5">
            {data.upcoming.slice(0, 3).map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border/40 last:border-0">
                <div className="min-w-0">
                  <div className="truncate">{c.proposal_payment?.lead_name || '—'}</div>
                  <div className="text-[11px] text-muted-foreground">{fmtDate(c.unlock_date)}</div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className="font-medium tabular-nums">{fmtEUR(c.amount)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 text-right">
        <Link to="/app/my-commissions" className="text-xs text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1" data-testid="mycomm-view-all">
          {t('commissions.seeAll', 'Vedi tutte le commissioni')} <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </Card>
  );
}

function BreakdownItem({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-lg bg-[var(--surface-2)]/60 border border-border p-2.5 text-center">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1 justify-center">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`font-medium mt-0.5 tabular-nums ${color}`}>{fmtEUR(value)}</div>
    </div>
  );
}
